import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import ModernCtaSection from '../components/common/ModernCtaSection';
import ContactSection from '../components/home/ContactSection';

const Contact = () => {
  // Initialize observer for scroll animations
  const [sectionRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section using ModernCtaSection */}
      <ModernCtaSection
        title="Ready to Revolutionize Your Legal Practice?"
        description="Join the growing number of forward-thinking law firms using Veritect.ai to gain a competitive edge. Schedule a demo today to see our AI-powered solutions in action."
        buttonText=""
        buttonLink=""
      >
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button 
            variant="primary" 
            size="lg"
            href="#contact-section"
            className="bg-blue-600 hover:bg-blue-700 shadow-lg relative overflow-hidden group"
          >
            <span className="relative z-10">Schedule a Demo</span>
            <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
          </Button>
          <Button 
            variant="outline" 
            size="lg"
            href="mailto:info@veritect.ai"
            className="text-white border-white hover:bg-white/10"
          >
            Contact Sales
          </Button>
        </div>
      </ModernCtaSection>

      {/* Stats Section */}
      <section className="py-16 relative overflow-hidden bg-primary-50">
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
            ref={sectionRef}
          >
            <span className="text-primary-600 font-semibold">Our Impact</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Transforming Legal Practice with AI
            </h2>
            <p className="text-dark-500 text-lg">
              See the real impact our solutions are making for law firms across the globe.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '85%', label: 'Time Saved on Research' },
              { number: '70%', label: 'Faster Contract Analysis' },
              { number: '92%', label: 'Client Satisfaction' },
              { number: '3x', label: 'Return on Investment' }
            ].map((stat, index) => (
              <motion.div 
                key={index} 
                className="text-center glass-effect p-6 rounded-xl border border-white/10 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2 gradient-text">{stat.number}</div>
                <div className="text-dark-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendly Section - Using updated component */}
      <ContactSection />
      
      {/* FAQ Section */}
      <section className="py-20 bg-primary-50">
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-dark-500 text-lg">
              Have questions? Find quick answers to common inquiries about our services and solutions.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "How long does implementation take?",
                answer: "Most firms are up and running with our solutions within 2-4 weeks, depending on the complexity of integration with your existing systems. Our implementation team handles technical setup and provides comprehensive training for your staff."
              },
              {
                question: "Is my data secure with Veritect.ai?",
                answer: "Absolutely. We employ bank-level encryption, maintain SOC 2 compliance, and implement stringent access controls. Your data never leaves your control, and we offer deployment options that keep sensitive information within your firm's security perimeter."
              },
              {
                question: "What types of law firms do you work with?",
                answer: "We work with firms of all sizes, from solo practitioners to global firms with hundreds of attorneys. Our solutions are scalable and can be customized to meet the specific needs of your practice area and firm size."
              },
              {
                question: "Do you offer custom solutions for specific practice areas?",
                answer: "Yes, we can customize our AI solutions for specific areas like intellectual property, corporate law, litigation, and many others. Our team works with you to understand your unique requirements and tailor our technology accordingly."
              }
            ].map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              >
                <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                  <p className="text-dark-600">{faq.answer}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Removed the final ModernCtaSection that was duplicating content */}
    </motion.div>
  );
};

export default Contact;